import { useState, useEffect } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import PasswordInput, { isPasswordValid } from '../components/PasswordInput';
import toast from 'react-hot-toast';

const SMTP_EMPTY = { host: '', port: '587', user: '', pass: '', from: '', secure: false, enabled: false };

export default function AdminSettings() {
  const { user } = useAuth();
  const [profile,  setProfile]  = useState({ name: '', email: '' });
  const [pwForm,   setPwForm]   = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [smtp,     setSmtp]     = useState(SMTP_EMPTY);
  const [testTo,   setTestTo]   = useState('');
  const { setBranding } = useBranding();
  const [brand,    setBrand]    = useState({ portalName: '', portalSub: '', logoData: '' });
  const [savingBr, setSavingBr] = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const [savingSm, setSavingSm] = useState(false);
  const [testing,  setTesting]  = useState(false);

  useEffect(() => {
    api.get('/auth/me').then(r => setProfile({ name: r.data.name, email: r.data.email }));
    api.get('/settings/smtp').then(r => setSmtp(r.data)).catch(() => {});
    api.get('/settings/branding').then(r => setBrand(r.data)).catch(() => {});
  }, []);

  async function handleSaveProfile(e) {
    e.preventDefault(); setSaving(true);
    try { await api.put('/auth/profile', profile); toast.success('Profilis atnaujintas'); }
    catch (err) { toast.error(err.response?.data?.error || 'Klaida'); }
    finally { setSaving(false); }
  }

  async function handleChangePassword(e) {
    e.preventDefault();
    if (!isPasswordValid(pwForm.newPassword)) { toast.error('Slaptažodis neatitinka reikalavimų'); return; }
    if (pwForm.newPassword !== pwForm.confirm) { toast.error('Slaptažodžiai nesutampa'); return; }
    setSavingPw(true);
    try {
      await api.put('/auth/password', { currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword });
      toast.success('Slaptažodis pakeistas!');
      setPwForm({ currentPassword: '', newPassword: '', confirm: '' });
    } catch (err) { toast.error(err.response?.data?.error || 'Neteisingas dabartinis slaptažodis'); }
    finally { setSavingPw(false); }
  }

  async function handleSaveBranding(e) {
    e.preventDefault(); setSavingBr(true);
    try {
      await api.put('/settings/branding', brand);
      setBranding(brand);
      toast.success('Išsaugota');
    } catch { toast.error('Klaida'); }
    finally { setSavingBr(false); }
  }

  function handleLogo(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 500*1024) { toast.error('Logotipas per didelis (max 500KB)'); return; }
    const reader = new FileReader();
    reader.onload = ev => setBrand(b => ({ ...b, logoData: ev.target.result }));
    reader.readAsDataURL(file);
  }

  async function handleSaveSmtp(e) {
    e.preventDefault(); setSavingSm(true);
    try { await api.put('/settings/smtp', smtp); toast.success('SMTP nustatymai išsaugoti'); }
    catch (err) { toast.error(err.response?.data?.error || 'Klaida'); }
    finally { setSavingSm(false); }
  }

  async function handleTestSmtp() {
    if (!testTo) { toast.error('Įveskite el. paštą testui'); return; }
    setTesting(true);
    try { const r = await api.post('/settings/smtp/test', { to: testTo }); toast.success(r.data.message); }
    catch (err) { toast.error(err.response?.data?.error || 'Klaida siunčiant'); }
    finally { setTesting(false); }
  }

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Administratoriaus nustatymai</div>
        <div className="page-subtitle">Profilis, slaptažodis ir el. pašto konfigūracija</div>
      </div>

      {/* BRANDING */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>🎨 Portalo išvaizda</div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>Pavadinimas ir logotipas rodomi šoninėje juostoje.</div>
        <form onSubmit={handleSaveBranding}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Portalo pavadinimas</label>
              <input className="form-input" value={brand.portalName} onChange={e => setBrand({...brand, portalName: e.target.value})} placeholder="Srauto Portalas" />
            </div>
            <div className="form-group">
              <label className="form-label">Antraštė (sub-title)</label>
              <input className="form-input" value={brand.portalSub} onChange={e => setBrand({...brand, portalSub: e.target.value})} placeholder="Axis Object Analytics" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Logotipas (PNG/JPG, max 500KB)</label>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              {brand.logoData && <img src={brand.logoData} alt="logo" style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover', border: '2px solid var(--border)' }} />}
              <input type="file" accept="image/*" onChange={handleLogo} className="form-input" style={{ flex: 1 }} />
              {brand.logoData && <button type="button" className="btn btn-sm btn-danger" onClick={() => setBrand(b => ({...b, logoData: ''}))}>✕ Ištrinti</button>}
            </div>
          </div>
          <button className="btn btn-primary" type="submit" disabled={savingBr}>
            {savingBr ? <span className="loading-spinner" /> : '💾 Išsaugoti išvaizdą'}
          </button>
        </form>
      </div>

      {/* SMTP */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>📧 El. pašto nustatymai (SMTP)</div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>Visi automatiniai laiškai bus siunčiami iš šio adreso.</div>
        <form onSubmit={handleSaveSmtp}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '12px 16px', background: 'var(--bg)', borderRadius: 8, border: '1px solid var(--border)', marginBottom: 20 }}>
            <input type="checkbox" checked={smtp.enabled} onChange={e => setSmtp({...smtp, enabled: e.target.checked})} style={{ width: 16, height: 16 }} />
            <div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>✅ El. pašto siuntimas įjungtas</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Jei išjungta – laiškai nebus siunčiami</div>
            </div>
          </label>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">SMTP serveris (Host)</label>
              <input className="form-input" value={smtp.host} onChange={e => setSmtp({...smtp, host: e.target.value})} placeholder="smtp.gmail.com" />
            </div>
            <div className="form-group" style={{ maxWidth: 140 }}>
              <label className="form-label">Prievadas (Port)</label>
              <input className="form-input" value={smtp.port} onChange={e => setSmtp({...smtp, port: e.target.value})} placeholder="587" type="number" />
            </div>
            <div className="form-group" style={{ maxWidth: 180 }}>
              <label className="form-label">Šifravimas</label>
              <select className="form-input" value={smtp.secure ? 'true' : 'false'} onChange={e => setSmtp({...smtp, secure: e.target.value === 'true'})}>
                <option value="false">STARTTLS (587)</option>
                <option value="true">SSL/TLS (465)</option>
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Vartotojas (Login)</label>
              <input className="form-input" type="email" value={smtp.user} onChange={e => setSmtp({...smtp, user: e.target.value})} placeholder="portalas@imone.lt" />
            </div>
            <div className="form-group">
              <label className="form-label">Slaptažodis</label>
              <input className="form-input" type="password" value={smtp.pass} onChange={e => setSmtp({...smtp, pass: e.target.value})} placeholder="Palikite tuščią jei nekeičiate" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Siuntėjo adresas (From)</label>
            <input className="form-input" type="email" value={smtp.from} onChange={e => setSmtp({...smtp, from: e.target.value})} placeholder="portalas@imone.lt" />
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Jei tuščia – naudojamas Login adresas</div>
          </div>
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>Greiti šablonai:</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {[
                { label: 'Gmail',    host: 'smtp.gmail.com',      port: '587', secure: false },
                { label: 'Outlook',  host: 'smtp.office365.com',  port: '587', secure: false },
                { label: 'SendGrid', host: 'smtp.sendgrid.net',   port: '587', secure: false },
                { label: 'Yahoo',    host: 'smtp.mail.yahoo.com', port: '465', secure: true  },
              ].map(t => (
                <button key={t.label} type="button" className="btn btn-sm btn-secondary"
                  onClick={() => setSmtp(s => ({ ...s, host: t.host, port: t.port, secure: t.secure }))}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" type="submit" disabled={savingSm}>
              {savingSm ? <span className="loading-spinner" /> : '💾 Išsaugoti nustatymus'}
            </button>
            <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
              <input className="filter-input" type="email" value={testTo} onChange={e => setTestTo(e.target.value)} placeholder="Testo el. paštas..." style={{ width: 220 }} />
              <button type="button" className="btn btn-secondary" onClick={handleTestSmtp} disabled={testing}>
                {testing ? <span className="loading-spinner" /> : '📧 Testuoti'}
              </button>
            </div>
          </div>
        </form>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        {/* Profilis */}
        <div className="card">
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>👤 Profilio duomenys</div>
          <form onSubmit={handleSaveProfile}>
            <div className="form-group">
              <label className="form-label">Vardas</label>
              <input className="form-input" value={profile.name} onChange={e => setProfile({...profile, name: e.target.value})} required />
            </div>
            <div className="form-group">
              <label className="form-label">El. paštas</label>
              <input className="form-input" type="email" value={profile.email} onChange={e => setProfile({...profile, email: e.target.value})} required />
            </div>
            <button className="btn btn-primary" type="submit" disabled={saving} style={{ width: '100%', justifyContent: 'center' }}>
              {saving ? <span className="loading-spinner" /> : '💾 Išsaugoti profilį'}
            </button>
          </form>
          <div className="divider" />
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            <div>🔑 Rolė: <strong>Administratorius</strong></div>
            <div style={{ marginTop: 4 }}>🆔 ID: {user?.id}</div>
          </div>
        </div>

        {/* Slaptažodis */}
        <div className="card">
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>🔐 Keisti slaptažodį</div>
          <form onSubmit={handleChangePassword}>
            <div className="form-group">
              <label className="form-label">Dabartinis slaptažodis</label>
              <input className="form-input" type="password" value={pwForm.currentPassword}
                onChange={e => setPwForm({...pwForm, currentPassword: e.target.value})} required />
            </div>
            <div className="form-group">
              <PasswordInput label="Naujas slaptažodis" value={pwForm.newPassword}
                onChange={e => setPwForm({...pwForm, newPassword: e.target.value})} required />
            </div>
            <div className="form-group">
              <label className="form-label">Pakartoti naują slaptažodį</label>
              <input className="form-input" type="password" value={pwForm.confirm}
                onChange={e => setPwForm({...pwForm, confirm: e.target.value})} required />
              {pwForm.confirm && pwForm.newPassword !== pwForm.confirm && (
                <div style={{ color: '#ef4444', fontSize: 11, marginTop: 4 }}>⚠️ Slaptažodžiai nesutampa</div>
              )}
            </div>
            <button className="btn btn-secondary" type="submit" disabled={savingPw} style={{ width: '100%', justifyContent: 'center' }}>
              {savingPw ? <span className="loading-spinner" /> : '🔑 Pakeisti slaptažodį'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
